import LivePreviewBase from "./live_preview_base"
import {
  applyTextStrokeToElements,
  applyFontSizeScale,
  applyTypographyToElements,
  applyTextShadowToElements,
} from "../utils/text_treatment"
import { TYPE_PAIR_SPECS } from "../services/language_learning_type_pairs"

// Live-preview controller for the False Friend wizard.
//
// The preview mirrors FRAME 0, and on this template frame 0 carries the whole
// gotcha: BOTH lookalike words painted, the letters they SHARE already tinted
// amber, a language chip on each card, the `≈` tie chip between them, the TRAP
// badge and the hook. Every applier here therefore REPAINTS something that is
// already visible. Four consequences, each a law rather than a style choice:
//
//   1. Nothing here may touch `opacity`, ever. The template has no fade-in and
//      no fade-out; the later-beat dim in the render is a colour mix and the
//      trap tint is a colour on a subset of letters.
//   2. Nothing here draws the ✗ verdict pill, the meaning panel or the fix
//      pill. All three are later-beat CONTENT that lands ON its own spoken cue
//      (the round-4 "reveals wait for the spoken answer" law) — the renderer is
//      tested on exactly that, and the preview states the same frame-0 truth by
//      simply having no pill to draw.
//   3. AMBER IS NOT THEMED. `--p-trap` is a fixed token: amber means "this is
//      the deception" and the theme accent means "this is the fix", so
//      applyTheme swaps the five palette properties and deliberately leaves
//      `--p-trap` alone. A signal that changed hue per theme would stop being a
//      signal.
//   4. The WORDS are deliberately NOT live-bound. The tint on both cards is
//      derived from both words TOGETHER, and that derivation already exists
//      twice — the renderer's shared `utils/diffSpans.ts` and the Ruby mirror
//      in ShortsWizard::Shared::WordContrast. A third copy in JS is precisely
//      how a preview starts lying about the render, so word edits repaint on
//      save (the T1/T2/T3 posture). Everything that does NOT feed the tint —
//      hook, CTA, the two languages, theme, typography, size, stroke, shadow —
//      updates live.
//
// The two language selects are genuinely asymmetric here, which is the whole
// template: `target_language_code` names the TRAP's language (hero flag,
// language label, upper chip) and `learner_locale` names the LOOKALIKE's (lower
// chip) — plus the locale every chrome string is resolved in, which is why it
// also repaints the badge and the label.
//
// TYPE_PAIR_SPECS is imported from the shared language-learning catalog
// (de-mirrored — the font data is authored once in the TS source and consumed by
// both legs), so the preview can never drift from what the renderer paints.
//
// Mounted alongside `palette-live-preview`, which owns the background layers +
// tint/darkness sliders. Inherits event wiring + dispatch + value normalisation
// from LivePreviewBase.
export default class extends LivePreviewBase {
  static targets = [
    "frame", "flag", "languageLabel", "trapBadge", "hookText",
    "trapWord", "trapChip",
    "tieChip",
    "lookalikeWord", "lookalikeChip",
    "ctaText",
  ]

  static FIELD_BINDINGS = {
    hook_text:            { apply: "applyHookText" },
    cta_text:             { apply: "applyCtaText" },
    target_language_code: { apply: "applyTargetLanguage" },
    learner_locale:       { apply: "applyLearnerLocale" },
    theme:                { apply: "applyTheme" },
    type_pair:            { apply: "applyTypography" },
    italic_override:      { apply: "applyTypography" },
    font_size_scale:      { apply: "applyFontSize" },
    display_override:     { apply: "applyFontSize" },
    body_override:        { apply: "applyFontSize" },
    text_shadow:          { apply: "applyTextShadow" },
    text_stroke_color:    { apply: "applyStroke" },
    text_stroke_width:    { apply: "applyStroke" },
  }

  // -- chrome ---------------------------------------------------------------

  // The hook is drawn the way the renderer draws it: language tags are an
  // authoring instruction for the take splitter, never ink. (T8's authoring law
  // says no payload carries one at all — every cross-language seam is a
  // builder-composed take boundary — so stripping is purely so a hand-edited
  // payload still draws clean.)
  applyHookText() {
    this.setText("hookText", stripLanguageTags(this.fieldValue("hook_text")) || "Your hook goes here.")
  }

  applyCtaText() { this.setText("ctaText", this.fieldValue("cta_text")) }

  // The TRAP's language: the hero flag, the language label, and the UPPER
  // card's chip. Base subtag throughout, so "en-GB" chips as "EN" and flags as
  // 🇬🇧 — matching FalseFriend::ConfigBuilder#resolved_target_flag_emoji, which
  // splits the subtag before the lookup. (There is exactly one hero flag on
  // this template; the cards carry language CHIPS, not flags, so the dialect
  // flag-pair question never arises.)
  applyTargetLanguage({ value }) {
    this.setText("languageLabel", languageName(value, this.learnerLocale()))
    if (this.hasFlagTarget) this.flagTarget.textContent = FLAGS[baseSubtag(value)] || "🏳️"
    this.setText("trapChip", baseSubtag(value).toUpperCase() || "??")
  }

  // The LOOKALIKE's language — by definition the viewer's own, which is why it
  // deceives, and why there is no third language picker on this template. It is
  // also the locale every chrome string resolves in, so the badge and the
  // language label repaint with it.
  applyLearnerLocale({ value }) {
    const locale = value || "es"
    this.setText("lookalikeChip", baseSubtag(locale).toUpperCase() || "??")
    this.setText("trapBadge", TRAP_BADGE[baseSubtag(locale)] || TRAP_BADGE.en)
    this.setText("languageLabel", languageName(this.fieldValue("target_language_code"), locale))
  }

  // The palette drives every ink on the frame: the words take `--p-text`, the
  // chips take `--p-muted`, the CTA pill takes `--p-accent`. Swapping the custom
  // properties repaints all of them without touching a single opacity.
  //
  // `--p-trap` is NOT in this list, on purpose (see law 3 above).
  applyTheme({ value }) {
    if (!this.hasFrameTarget) return
    const palette = THEME_PALETTES[value] || THEME_PALETTES.notebook
    const f = this.frameTarget
    f.style.setProperty("--p-bg", palette.bg)
    f.style.setProperty("--p-surface", palette.surface)
    f.style.setProperty("--p-text", palette.text)
    f.style.setProperty("--p-muted", palette.muted)
    f.style.setProperty("--p-accent", palette.accent)
  }

  // -- typography / font-size / stroke --------------------------------------

  // The two words are DISPLAY role alongside the hook — they are the hero of the
  // frame, and the card anatomy has nothing else competing with them.
  applyTypography() {
    const spec = TYPE_PAIR_SPECS[this.fieldValue("type_pair") || "default"]
    if (!spec) return
    const display = []
    if (this.hasHookTextTarget) display.push(this.hookTextTarget)
    for (const el of this.wordTargets()) display.push(el)
    applyTypographyToElements(
      { display, body: this.bodyTargets() },
      spec,
      this.fieldValue("italic_override") || "auto",
    )
  }

  applyFontSize() {
    const globalScale = positiveOr(this.fieldValue("font_size_scale"), 1)
    const displayScale = this.readPositiveOverride("display_override") ?? globalScale
    const bodyScale    = this.readPositiveOverride("body_override")    ?? globalScale

    const displayPairs = []
    if (this.hasHookTextTarget) displayPairs.push(sizedPair(this.hookTextTarget, 13))
    for (const el of this.wordTargets()) displayPairs.push(sizedPair(el, 34))
    applyFontSizeScale(displayPairs, displayScale)

    const bodyPairs = this.bodyTargets().map((el) => sizedPair(el, 10))
    applyFontSizeScale(bodyPairs, bodyScale)
  }

  // Stroke rides the opted-in elements only — the hook + the two words. The
  // badge, the chips, the tie chip and the CTA pill sit on the theme surface and
  // skip, mirroring the renderer's applyStroke: false on those roles.
  applyStroke() {
    const targets = Array.from(
      this.element.querySelectorAll("[data-stroke-target]"),
    ).map((el) => ({
      element: el,
      basePx: parseFloat(el.dataset.strokeBasePx) || undefined,
      skip: el.dataset.strokeSkip === "true",
    }))
    if (!targets.length) return
    applyTextStrokeToElements(targets, {
      width: this.fieldValue("text_stroke_width"),
      color: this.fieldValue("text_stroke_color") || "#000000",
    })
  }

  applyTextShadow({ value }) {
    applyTextShadowToElements(
      Array.from(this.element.querySelectorAll("[data-stroke-target]")),
      value,
    )
  }

  readPositiveOverride(fieldName) {
    for (const el of this.fieldElements(fieldName)) {
      if (el.type === "range" && !el.disabled) {
        const v = parseFloat(el.value)
        return Number.isFinite(v) && v > 0 ? v : null
      }
    }
    return null
  }

  // -- preview DOM helpers --------------------------------------------------

  learnerLocale() {
    return this.fieldValue("learner_locale") || "es"
  }

  wordTargets() {
    return [
      this.hasTrapWordTarget ? this.trapWordTarget : null,
      this.hasLookalikeWordTarget ? this.lookalikeWordTarget : null,
    ].filter(Boolean)
  }

  // Everything on the theme surface: the TRAP badge, the two language chips,
  // the tie chip and the CTA pill.
  bodyTargets() {
    return [ "trapBadge", "trapChip", "tieChip", "lookalikeChip", "ctaText" ]
      .filter((name) => this[`has${capitalize(name)}Target`])
      .map((name) => this[`${name}Target`])
  }

  setText(targetName, value) {
    const has = `has${capitalize(targetName)}Target`
    if (this[has]) this[`${targetName}Target`].textContent = value
  }
}

function capitalize(name) {
  return name[0].toUpperCase() + name.slice(1)
}

// "en-GB" → "en". The cards chip the BASE subtag, and the flag/name lookups are
// keyed on it too.
function baseSubtag(code) {
  return (code || "").toString().split("-")[0].toLowerCase()
}

// The label is localized in the VIEWER's language, mirroring
// LanguageNameResolver.call(code, locale: learner_locale) on the server — a
// Modo video (viewer L1 = es) reads "Inglés", not "English".
function languageName(code, learnerLocale) {
  const base = baseSubtag(code)
  const table = LANGUAGE_NAMES[baseSubtag(learnerLocale)] || LANGUAGE_NAMES.en
  return table[base] || LANGUAGE_NAMES.en[base] || base.toUpperCase()
}

// Every scaled element carries its renderer-relative base size in
// `data-base-font-size` (server-rendered), so the JS never restates the
// preview's size table — the fallback is only for a hand-built DOM.
function sizedPair(element, fallbackBasePx) {
  return { element, basePx: parseFloat(element.dataset.baseFontSize) || fallbackBasePx }
}

function positiveOr(value, fallback) {
  if (value === null || value === undefined) return fallback
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

// Authoring language spans (<en>…</en>, <es>…</es>) split the spoken take;
// they are never drawn. Mirrors LanguageRuns.strip_tags on the Ruby side.
function stripLanguageTags(text) {
  return (text || "").replace(/<\/?[a-zA-Z][a-zA-Z-]*>/g, "").replace(/\s+/g, " ").trim()
}

// All seven language-learning looks, including the two channel-locked ones —
// a channel-bound config CAN carry `cafecito` / `modo`, and repainting it as
// notebook would be the preview lying about the render.
const THEME_PALETTES = {
  "notebook":   { "bg": "#FAF7EC", "surface": "#FFFFFF", "text": "#1F2937", "muted": "#6B7280", "accent": "#E8A838" },
  "chalkboard": { "bg": "#1F2A24", "surface": "#243530", "text": "#F4F3EC", "muted": "#C5C2AE", "accent": "#FFE066" },
  "whiteboard": { "bg": "#F8FAFC", "surface": "#FFFFFF", "text": "#111827", "muted": "#4B5563", "accent": "#DC2626" },
  "cork_board": { "bg": "#C08B5C", "surface": "#FEF3C7", "text": "#1C1917", "muted": "#57534E", "accent": "#EAB308" },
  "minimal":    { "bg": "#FFFFFF", "surface": "#F8FAFC", "text": "#0F172A", "muted": "#64748B", "accent": "#6366F1" },
  "cafecito":   { "bg": "#F6EDDF", "surface": "#FFFBF2", "text": "#3B2A1E", "muted": "#6E5A47", "accent": "#C98A2D" },
  "modo":       { "bg": "#1E2733", "surface": "#28323F", "text": "#F2F5F9", "muted": "#A8B4C2", "accent": "#FFB020" },
}

// The franchise pill, mirroring `niche_templates.false_friend.trap_badge` in
// the three shipped locale files.
const TRAP_BADGE = {
  "en": "FALSE FRIEND",
  "es": "FALSO AMIGO",
}

// Mirrors config/locales/language_names.{en,es}.yml for the languages the
// content step offers. An unknown pair falls back to the English name, then to
// the upper-cased code — the same degrade the server's `default:` gives.
const LANGUAGE_NAMES = {
  en: {
    "ar": "Arabic", "de": "German", "en": "English", "es": "Spanish", "fr": "French",
    "hi": "Hindi", "it": "Italian", "ja": "Japanese", "ko": "Korean", "nl": "Dutch",
    "pl": "Polish", "pt": "Portuguese", "ru": "Russian", "sv": "Swedish", "th": "Thai",
    "tr": "Turkish", "vi": "Vietnamese", "zh": "Chinese",
  },
  es: {
    "ar": "Árabe", "de": "Alemán", "en": "Inglés", "es": "Español", "fr": "Francés",
    "hi": "Hindi", "it": "Italiano", "ja": "Japonés", "ko": "Coreano", "nl": "Neerlandés",
    "pl": "Polaco", "pt": "Portugués", "ru": "Ruso", "sv": "Sueco", "th": "Tailandés",
    "tr": "Turco", "vi": "Vietnamita", "zh": "Chino",
  },
}

const FLAGS = {
  "ar": "🇸🇦", "de": "🇩🇪", "en": "🇬🇧", "es": "🇪🇸", "fr": "🇫🇷",
  "hi": "🇮🇳", "it": "🇮🇹", "ja": "🇯🇵", "ko": "🇰🇷", "nl": "🇳🇱",
  "pl": "🇵🇱", "pt": "🇵🇹", "ru": "🇷🇺", "sv": "🇸🇪", "th": "🇹🇭",
  "tr": "🇹🇷", "vi": "🇻🇳", "zh": "🇨🇳",
}
